import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import ChurchRoundedIcon from '@mui/icons-material/ChurchRounded'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded'
import { toast } from 'react-toastify'
import { imageDefaults } from 'components/utils/imageDefaults'
import {
  bootstrapInitialChurchThreadKeyVersion,
  decryptChurchThreadMessages,
  fetchChurchThreadMessages,
  rotateChurchThreadKeyIfRequired,
  sendEncryptedChurchThreadText,
  updateChurchThreadPostingEnabled,
} from 'services/churchThreadEncryptionService'
import { socketService } from 'services/socketService'
import { useTranslation } from 'react-i18next'

const PAGE_SIZE = 40
const errorCode = (error) => String(error?.response?.data?.code || '')
const friendlyError = (error, t) => {
  const code = errorCode(error)
  if (code === 'CHURCH_THREAD_KEY_ROTATION_REQUIRED') return t('churchThread.encryptionUpdatingRetry')
  if (code === 'CHURCH_THREAD_POSTING_PAUSED') return t('churchThread.postingPaused')
  if (code === 'CHURCH_THREAD_POSTING_FORBIDDEN') return t('churchThread.postingUnavailable')
  if (code === 'CHURCH_THREAD_WRAPPED_KEY_NOT_FOUND') return t('churchThread.notReady')
  return error?.response?.data?.message || error?.message || t('churchThread.unavailable')
}

export default function ChurchThreadPanel() {
  const { t, i18n } = useTranslation('messaging')
  const { churchId } = useParams()
  const navigate = useNavigate()
  const bottomRef = useRef(null)
  const loadingRef = useRef(false)
  const rotationRef = useRef(null)
  const { user } = useSelector((state) => state.auth)
  const { memberOf } = useSelector((state) => state.memberships2)

  const savedChurch = useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem(`church-thread:${churchId}`)) || {} } catch { return {} }
  }, [churchId])
  const church = useMemo(() => {
    if (user?.isOrganization && String(user._id) === String(churchId)) return user
    return (memberOf || []).find((item) => String(item?._id || item?.id) === String(churchId)) || savedChurch
  }, [churchId, memberOf, savedChurch, user])
  const churchName = church?.fullName || church?.name || savedChurch?.name || t('participants.church')
  const churchImage = church?.profileImg?.url || church?.profileImage?.url || savedChurch?.image || imageDefaults.avatar
  const churchPublicKey = church?.publicKey || savedChurch?.publicKey || (user?.isOrganization ? user?.publicKey : null)
  const accountId = String(user?._id || '')
  const token = user?.token || ''
  const isChurchProfile = Boolean(user?.isOrganization && accountId === String(churchId))

  const [messages, setMessages] = useState([])
  const [thread, setThread] = useState(null)
  const [text, setText] = useState('')
  const [loading, setLoading] = useState(true)
  const [loadingOlder, setLoadingOlder] = useState(false)
  const [sending, setSending] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [nextCursor, setNextCursor] = useState(null)
  const [status, setStatus] = useState('')
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [settingsBusy, setSettingsBusy] = useState(false)
  const [ownPostingRestricted, setOwnPostingRestricted] = useState(false)

  const rotateIfNeeded = useCallback(async () => {
    if (!isChurchProfile || !churchId || !token || !accountId) return false
    if (rotationRef.current) return rotationRef.current
    const request = (async () => {
      const result = await rotateChurchThreadKeyIfRequired({ churchId, token, accountId })
      if (result.status === 'blocked') {
        setStatus(t('churchThread.waitingForKeys'))
        return false
      }
      if (result.status === 'rotated') {
        setThread(result.commitResult?.thread || null)
        setStatus('')
        return true
      }
      return false
    })()
    rotationRef.current = request
    try { return await request } finally { rotationRef.current = null }
  }, [accountId, churchId, isChurchProfile, token])

  const loadMessages = useCallback(async ({ cursor = null, appendOlder = false } = {}) => {
    if (!churchId || !token || !accountId || loadingRef.current) return
    loadingRef.current = true
    if (appendOlder) setLoadingOlder(true)
    else setLoading(true)
    let waitingForKeys = false
    try {
      if (isChurchProfile && !appendOlder) {
        const bootstrap = await bootstrapInitialChurchThreadKeyVersion({ churchId, token, accountId })
        if (bootstrap.status === 'no_recipients_with_public_keys') {
          waitingForKeys = true
          setStatus(t('churchThread.waitingForKeys'))
        }
        await rotateIfNeeded()
      }
      const page = await fetchChurchThreadMessages({ churchId, token, cursor, limit: PAGE_SIZE })
      setThread(page.thread || null)
      const decrypted = await decryptChurchThreadMessages({ messages: page.messages || [], token, accountId, churchPublicKey })
      setMessages((current) => appendOlder
        ? [...decrypted.filter((item) => !current.some((existing) => existing.id === item.id)), ...current]
        : decrypted)
      setNextCursor(page.pagination?.nextCursor || null)
      setHasMore(Boolean(page.pagination?.hasMore && page.pagination?.nextCursor))
      if (!waitingForKeys) setStatus('')
    } catch (error) {
      setStatus(friendlyError(error, t))
      if (!appendOlder) setMessages([])
    } finally {
      loadingRef.current = false
      setLoading(false)
      setLoadingOlder(false)
    }
  }, [accountId, churchId, churchPublicKey, isChurchProfile, rotateIfNeeded, token])

  useEffect(() => { loadMessages() }, [loadMessages])

  useEffect(() => {
    if (!token || !accountId || !churchId) return undefined
    socketService.connect({ token, accountId, isOrganization: user?.isOrganization })
    socketService.joinChurchThread(churchId)

    const offMessage = socketService.on('churchThread:message:new', async (payload) => {
      if (String(payload?.churchId) !== String(churchId)) return
      const raw = payload.message || {}
      const normalized = { ...raw, id: String(raw.id || raw._id), churchId: String(raw.churchId || churchId), keyVersion: Number(raw.keyVersion) }
      const [incoming] = await decryptChurchThreadMessages({ messages: [normalized], token, accountId, churchPublicKey })
      if (!incoming) return
      setMessages((current) => current.some((item) => item.id === incoming.id) ? current : [...current, incoming])
    })
    const offPosting = socketService.on('churchThread:posting:changed', (payload) => {
      if (String(payload?.churchId) === String(churchId)) setThread((current) => current ? { ...current, postingEnabled: payload.postingEnabled } : current)
    })
    const offMemberPosting = socketService.on('churchThread:memberPosting:changed', (payload) => {
      if (!isChurchProfile && String(payload?.churchId) === String(churchId) && String(payload?.memberId) === accountId) {
        setOwnPostingRestricted(payload.canPost === false)
      }
    })
    const offKeyVersion = socketService.on('churchThread:keyVersion:changed', (payload) => {
      if (String(payload?.churchId) === String(churchId)) loadMessages()
    })
    const offRotation = socketService.on('churchThread:rotation:required', (payload) => {
      if (String(payload?.churchId) !== String(churchId)) return
      setStatus(t('churchThread.encryptionUpdating'))
      if (isChurchProfile) rotateIfNeeded().then(() => loadMessages())
    })
    const offReconnect = socketService.on('reconnect', () => {
      socketService.joinChurchThread(churchId)
      loadMessages()
    })

    return () => {
      offMessage(); offPosting(); offMemberPosting(); offKeyVersion(); offRotation(); offReconnect()
      socketService.leaveChurchThread(churchId)
    }
  }, [accountId, churchId, churchPublicKey, isChurchProfile, loadMessages, rotateIfNeeded, token, user?.isOrganization])

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages.length])

  const sendMessage = async (event) => {
    event.preventDefault()
    const plaintext = text.trim()
    if (!plaintext || sending || !thread?.currentKeyVersion || (thread.postingEnabled === false && !isChurchProfile)) return
    setSending(true)
    setText('')
    try {
      const result = await sendEncryptedChurchThreadText({
        churchId, token, accountId, currentKeyVersion: thread.currentKeyVersion, plaintext, churchPublicKey,
      })
      const [sent] = await decryptChurchThreadMessages({ messages: [result.message], token, accountId, churchPublicKey })
      if (sent) setMessages((current) => current.some((item) => item.id === sent.id) ? current : [...current, sent])
    } catch (error) {
      setText(plaintext)
      if (errorCode(error) === 'CHURCH_THREAD_POSTING_FORBIDDEN') setOwnPostingRestricted(true)
      if (errorCode(error) === 'CHURCH_THREAD_KEY_ROTATION_REQUIRED' && isChurchProfile) await rotateIfNeeded()
      toast.error(friendlyError(error, t))
    } finally {
      setSending(false)
    }
  }

  const togglePosting = async () => {
    if (!isChurchProfile || settingsBusy) return
    setSettingsBusy(true)
    try {
      const result = await updateChurchThreadPostingEnabled({ churchId, token, postingEnabled: !thread?.postingEnabled })
      setThread(result.thread)
      setSettingsOpen(false)
    } catch (error) {
      toast.error(friendlyError(error, t))
    } finally {
      setSettingsBusy(false)
    }
  }

  const composerDisabled = !thread?.currentKeyVersion || ownPostingRestricted || (thread?.postingEnabled === false && !isChurchProfile)

  return (
    <Panel>
      <MobileBack type='button' onClick={() => navigate('/messages')}><ArrowBackRoundedIcon /> {t('churchThread.messages')}</MobileBack>
      <Header>
        <ChurchIdentity><img src={churchImage} alt='' /><div><h1>{churchName}</h1><p><LockRoundedIcon /> {t('churchThread.encryptedMessages')}</p></div></ChurchIdentity>
        {isChurchProfile && <SettingsButton type='button' aria-label={t('churchThread.settings')} onClick={() => setSettingsOpen((open) => !open)}><SettingsRoundedIcon /></SettingsButton>}
        {settingsOpen && <SettingsMenu><strong>{t('churchThread.settings')}</strong><button type='button' disabled={settingsBusy} onClick={togglePosting}>{thread?.postingEnabled === false ? t('churchThread.allowMemberPosting') : t('churchThread.pausePosting')}</button></SettingsMenu>}
      </Header>
      {status && <Notice>{status}</Notice>}
      {thread?.postingEnabled === false && isChurchProfile && <Notice>{t('churchThread.pausedProfileNotice')}</Notice>}
      <MessageArea>
        {hasMore && <LoadOlder type='button' disabled={loadingOlder} onClick={() => loadMessages({ cursor: nextCursor, appendOlder: true })}>{loadingOlder ? t('churchThread.loadingOlder') : t('churchThread.loadOlder')}</LoadOlder>}
        {loading ? <CenterState>{t('churchThread.loading')}</CenterState> : messages.length === 0 ? <CenterState><ChurchRoundedIcon /><strong>{t('churchThread.emptyTitle')}</strong><span>{status || t('churchThread.allEncrypted')}</span></CenterState> : messages.map((message) => {
          const mine = String(message.senderId) === accountId
          const senderProfile = message.senderProfile || message.senderUser || message.sender || message.author
          const memberName = senderProfile?.fullName || senderProfile?.name || senderProfile?.username
          const sender = message.senderType === 'church' ? churchName : mine ? t('participants.you') : memberName || t('participants.member')
          return <MessageRow key={message.id} $mine={mine}><BubbleColumn $mine={mine}><Sender $mine={mine}>{sender}</Sender><Bubble $mine={mine} $unavailable={message.decryptStatus === 'unavailable'}>{message.text}</Bubble><Time>{message.createdAt ? new Intl.DateTimeFormat(i18n.language, { hour: 'numeric', minute: '2-digit' }).format(new Date(message.createdAt)) : ''}</Time></BubbleColumn></MessageRow>
        })}
        <div ref={bottomRef} />
      </MessageArea>
      {composerDisabled ? <DisabledComposer>{ownPostingRestricted ? t('churchThread.postingUnavailable') : thread?.postingEnabled === false ? t('churchThread.postingPaused') : t('churchThread.encryptionNotReady')}</DisabledComposer> : (
        <Composer onSubmit={sendMessage}><textarea value={text} onChange={(event) => setText(event.target.value)} placeholder={t('churchThread.placeholder')} maxLength={5000} aria-label={t('churchThread.placeholder')} /><button type='submit' disabled={sending || !text.trim()} aria-label={t('churchThread.send')}><SendRoundedIcon /></button></Composer>
      )}
    </Panel>
  )
}

const Panel = styled.main`position: relative; width: 700px; min-width: 550px; height: 100%; display: flex; flex-direction: column; overflow: hidden; border-radius: 25px; background: white; box-shadow: 0 0 5px #ccc; @media(max-width:1268px){min-width:400px;width:550px;} @media(max-width:768px){width:100%;min-width:0;border-radius:20px;}`
const Header = styled.header`position: relative; flex:0 0 auto; min-height:76px; display:flex; align-items:center; justify-content:space-between; padding:12px 18px; border-bottom:1px solid #e8ecee;`
const ChurchIdentity = styled.div`display:flex;align-items:center;gap:11px;min-width:0; img{width:44px;height:44px;border-radius:50%;object-fit:cover;} h1{margin:0;color:#334044;font-size:15px;} p{display:flex;align-items:center;gap:4px;margin:4px 0 0;color:#788387;font-size:10px;} svg{font-size:11px;}`
const SettingsButton = styled.button`width:38px;height:38px;display:grid;place-items:center;border:0;border-radius:50%;background:#edf1f2;color:#526064;cursor:pointer;&:hover{background:#e2e8ea;}`
const SettingsMenu = styled.div`position:absolute;z-index:5;top:60px;right:16px;width:210px;padding:12px;border:1px solid #e1e6e8;border-radius:12px;background:white;box-shadow:0 16px 40px rgba(20,35,40,.16);strong{display:block;margin-bottom:8px;color:#354145;font-size:12px;}button{width:100%;padding:10px;border:0;border-radius:8px;background:#edf1f2;color:#354145;cursor:pointer;text-align:left;}`
const Notice = styled.div`flex:0 0 auto;padding:9px 14px;background:#eef8f9;color:#536367;font-size:11px;text-align:center;`
const MessageArea = styled.section`flex:1;min-height:0;overflow-y:auto;padding:18px;background:#f6f8f9;scrollbar-width:thin;`
const CenterState = styled.div`height:100%;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:7px;color:#788387;text-align:center;svg{font-size:40px;color:#a0abad;}strong{color:#435055;}span{font-size:12px;}`
const LoadOlder = styled.button`display:block;margin:0 auto 16px;padding:8px 14px;border:0;border-radius:18px;background:#e5eaec;color:#526064;cursor:pointer;font-size:11px;font-weight:700;`
const MessageRow = styled.div`display:flex;justify-content:${({$mine})=>$mine?'flex-end':'flex-start'};margin:8px 0;`
const BubbleColumn = styled.div`max-width:74%;display:flex;flex-direction:column;align-items:${({$mine})=>$mine?'flex-end':'flex-start'};`
const Sender = styled.span`margin:0 7px 4px;color:#7b8589;font-size:10px;text-align:${({$mine})=>$mine?'right':'left'};`
const Bubble = styled.div`padding:10px 13px;border-radius:${({$mine})=>$mine?'16px 16px 4px 16px':'16px 16px 16px 4px'};background:${({$mine})=>$mine?'rgba(0,180,204,.16)':'white'};color:${({$unavailable})=>$unavailable?'#8a9498':'#293539'};font-size:13px;line-height:1.45;font-style:${({$unavailable})=>$unavailable?'italic':'normal'};box-shadow:0 1px 2px rgba(20,35,40,.08);white-space:pre-wrap;word-break:break-word;`
const Time = styled.span`min-height:13px;margin:3px 6px 0;color:#9aa3a6;font-size:9px;`
const Composer = styled.form`flex:0 0 auto;display:flex;align-items:flex-end;gap:9px;padding:12px 14px;border-top:1px solid #e8ecee;background:white;textarea{flex:1;min-height:42px;max-height:120px;resize:vertical;padding:11px 14px;border:1px solid #dfe5e7;border-radius:20px;outline:none;background:#f1f4f5;font-family:inherit;font-size:13px;&:focus{border-color:var(--themeColor);box-shadow:0 0 0 3px rgba(0,180,204,.1);}}button{width:42px;height:42px;display:grid;place-items:center;border:0;border-radius:50%;background:var(--themeColor);color:white;cursor:pointer;}button:disabled{opacity:.45;cursor:default;}`
const DisabledComposer = styled.div`flex:0 0 auto;margin:12px;padding:12px;border-radius:12px;background:#eef1f2;color:#727d81;font-size:12px;text-align:center;`
const MobileBack = styled.button`display:none;@media(max-width:768px){display:flex;align-items:center;gap:5px;padding:10px 14px;border:0;background:white;color:var(--themeColor);font-weight:700;}`
